import { useState, useEffect, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from 'react-router-dom';
import { Button, Typography, IconButton } from '@material-ui/core';
import moment from 'moment';
import { connect } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import rou from '../../../../router';
import { DropLeftIcon, DropRightIcon } from '../../../../components/Icon';
import Alert from '../../../../components/Alert';
import { i18n } from '../../../../language';
import { dutyAssignAPI } from '../../../../apis';
import DatePicker from '../Header/DatePicker';
import reorder from '../../../../utils/reorder';
import { authNameE } from '../../../../utils/userFuncs';
import { store } from '../../../../redux';
import Header from './head';
import Day from './day';
import AddCard from './addCard';
import Card from './card';



const useStyles = makeStyles((theme) => ({
  root: {},
  body: {
    padding: '6.25rem 3.438rem 0',
    background: theme.palette.font.white,
    position: 'relative',
  },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '2.25rem',
    letterSpacing: '-0.0625rem',
    color: '#001d63',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  rightHead: {
    display: 'flex',
    alignItems: 'center',
  },
  editButton: {
    fontFamily: 'NotoSans-Medium',
    marginLeft: '0.062rem',
    fontSize: '1rem',
    lineHeight: '1.25rem',
    color: theme.palette.font.white,
    fontWeight: '500',
    textTransform: 'inherit',
    borderRadius: '0.625rem',
    backgroundColor: '#4c93ff',
    padding: '0.875rem 1.25rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#4c93ff',
      cursor: 'pointer',
      textDecoration: 'none',
    },
  },
  dayButton: {
    width: '5.938rem',
    height: '3rem',
    padding: '0.75rem 1rem 0.75rem 1.125rem',
    marginRight: '0.375rem',
    borderRadius: '0.668rem',
    border: '0.081rem solid #e4e7eb',
    backgroundColor: theme.palette.font.white,
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    color: theme.palette.font.main,
    fontWeight: '500',
    textTransform: 'inherit',
    '&:hover': {
      backgroundColor: theme.palette.font.white,
    },
  },
  smallTitle: {
    marginTop: '0.313rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: theme.palette.font.sub,
    fontWeight: '500',
    lineHeight: '1.25rem',
    marginBottom: '1.875rem',
  },
  leftButton: {
    width: '3rem',
    height: '3rem',
    margin: '0 1.25rem',
    backgroundColor: theme.palette.font.white,
    borderRadius: '0.668rem',
    border: '0.081rem solid #e4e7eb',
    '&:hover': {
      backgroundColor: theme.palette.font.white,
    },
  },
  dropleft: {
    fontSize: '0.563rem',
    color: theme.palette.primary.main,
  },
  date: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.125rem',
    color: theme.palette.font.main,
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  plan: {
    width: '100%',
    display: 'flex',
  },
}));

function Week(firstDay) {
  let weekInit = [{}, {}, {}, {}, {}, {}, {}];
  for (let i = 0; i < 7; i++) {
    weekInit[i].date = moment(firstDay).add(i, 'd').date();
    weekInit[i].time = moment(firstDay).add(i, 'd').format('D/M/YYYY');
  }

  const day = moment().day();
  if (moment(moment(firstDay).format('YYYY-MM-DD')).isSame(moment(moment().day(0)).format('YYYY-MM-DD'), 'days')) {
    weekInit[day].mark = true;
  }
  return weekInit;
}



function Head({ DateStr, handleToday, handlePreviousWeek, handleNextWeek, title, children, id }) {
  const classes = useStyles();
  return (
    <div className={classes.head}>
      <div className={classes.title}>{title}</div>
      <div className={classes.rightHead} id={id}>
        <Button className={classes.dayButton} onClick={handleToday} id="DutyAssign-Button-afl">
          {i18n.t('DUTYASSIGN_RELIEVING_TODAY')}
        </Button>
        <IconButton className={classes.leftButton} onClick={handlePreviousWeek} id="DutyAssign-IconButton-afm">
          <DropLeftIcon className={classes.dropleft} />
        </IconButton>
        <Typography className={classes.date}>{DateStr()}</Typography>
        <IconButton className={classes.leftButton} onClick={handleNextWeek} id="DutyAssign-IconButton-afn">
          <DropRightIcon className={classes.dropleft} />
        </IconButton>
        {children}
      </div>
    </div>
  );
}

function RosterPlanner({ rouList }) {
  const classes = useStyles();
  const dutyDateInfo = JSON.parse(sessionStorage.getItem('dutyInfo'));
  const [selectDate, setSelectDate] = useState(dutyDateInfo.date ?? moment());
  const [firstDay, setFirstDay] = useState(moment(selectDate).day(0));
  const [weekList, setWeekList] = useState(Week(firstDay));
  const [rosterList, setRosterList] = useState(null);
  const [wardList, setWardList] = useState(null);
  const [weekDay, setWeekDay] = useState({
    start: moment(firstDay).format('D/M/YYYY'),
    end: moment(firstDay).add(6, 'd').format('D/M/YYYY'),
  });
  const [roster, setRoster] = useState([]);
  const [deleteSuccessDialogOpen, setDeleteSuccessDialogOpen] = useState(false);
  const [copySuccessDialogOpen, setCopySuccessDialogOpen] = useState(false);
  
    
  // getRosterAPI
  const getRosterAPI = useCallback(
    async (start = weekDay.start, end = weekDay.end) => {
      try {
        const timeStart = moment(start, 'D/M/YYYY').format('DD-MM-YYYY');
        const timeEnd = moment(end, 'D/M/YYYY').format('DD-MM-YYYY');
        const { result } = await dutyAssignAPI.getTemplateRoster({
          timeStart,
          timeEnd,
        });
        setRosterList(result);
      } catch (error) {
        console.log('-----Update Duty error----->', error);
      }
    },
    [weekDay]
  );

  const handleBatchDelete = async () => {
    try {
      const timeStart = moment(weekDay.start, 'D/M/YYYY').format('DD-MM-YYYY');
      const timeEnd = moment(weekDay.end, 'D/M/YYYY').format('DD-MM-YYYY');
      const { result } = await dutyAssignAPI.getTemplateRoster({
        timeStart,
        timeEnd,
      });
      setRosterList(result);
      setDeleteSuccessDialogOpen(true);
    } catch (error) {
      console.log('-----Update Duty error----->', error);
    }
  };

  const handleCopy = async () => {
    try {
      const timeStart = moment(weekDay.start, 'D/M/YYYY').format('DD-MM-YYYY');
      const timeEnd = moment(weekDay.end, 'D/M/YYYY').format('DD-MM-YYYY');
      const { result } = await dutyAssignAPI.getTemplateRoster({
        timeStart,
        timeEnd,
      });
      setRosterList(result);
      setCopySuccessDialogOpen(true);
    } catch (error) {
      console.log('-----Update Duty error----->', error);
    }
  };

  // getWardAPI
  const getWardAPI = async () => {
    try {
      const { result } = await dutyAssignAPI.getTemplateWard();
      setWardList([...result]);
    } catch (error) {
      console.log('-----Get Ward error----->', error);
    }
  };

  // updateAPI
  const updateTemplateAPI = async ({ id, text }) => {
    try {
      await dutyAssignAPI.postUpdateTemplateRoster({ dutyTemplateRosterId: id, rosterCode: text });

      await getRosterAPI();
    } catch (error) {
      console.log('-----Update Template error----->', error);
    }
  };
  const roleMap = store.getState()?.roleMap || {};
  // createAPI
  const createTemplateAPI = async ({
    addTime,
    dayOffWeek,
    dutyTemplateWardId,
    rosterCode,
    rosterName,
    defaultDutyRosterGroup = 'On',
    rosterOrder,
    endTime,
    isSmartComm = roleMap[authNameE.dutyAssign_Enable_Smart_Communication],
    
  }) => {
    try {
      
      await dutyAssignAPI.postCreateTemplateRoster({
        addTime,
        dayOffWeek,
        dutyTemplateWardId,
        rosterCode,
        rosterName,
        defaultDutyRosterGroup,
        rosterOrder,
        endTime,
        isSmartComm,
        
      });

      await getRosterAPI();
    } catch (error) {
      console.log('-----Create Template error----->', error);
    }
  };

  // deleteAPI
  const deleteTemplateAPI = async ({ id }) => {
    try {
      await dutyAssignAPI.postDeleteTemplateRoster({ dutyTemplateRosterId: id });
      await getRosterAPI();
    } catch (error) {
      console.log('-----Delete Template error----->', error);
    }
  };

  // orderAPI
  const updateTemplateOrderAPI = async (rosterOrder) => {
    try {
      await dutyAssignAPI.postUpdatePlannerOrder({ rosterOrder });
      await getRosterAPI();
    } catch (error) {
      console.log('-----Update Template Order error----->', error);
    }
  };

  useEffect(() => {
    (async () => {
      await getRosterAPI();
    })();
    (async () => {
      await getWardAPI();
    })();
  }, [rouList]);

  const list = useMemo(() => {
    let templatelist = [];
    let wardlist = [];

    if (Array.isArray(rosterList) && rosterList.length > 0) {
      templatelist = _.map(rosterList, (item) => {
        return {
          id: item.dutyTemplateRosterId,
          title: item.rosterCode,
          context: item.templateName,
          template: item.templateName,
          date: moment(item.startTime, 'DD-MM-YYYY').format('D/M/YYYY'),
          rosterOrder: item.rosterOrder,
        };
      });
    }

    if (Array.isArray(wardList) && wardList.length > 0) {
      wardlist = _.map(
        wardList.filter((item) => {
          return !item.isDeleted;
        }),
        (item) => {
          return {
            dutyTemplateWardId: item.dutyTemplateWardId,
            templateName: item.templateName
          };
        }
      );
    }

    setRoster(templatelist);

    return { templatelist, wardlist };
  }, [rosterList, wardList]);

  function DateStr() {
    return `${weekDay.start}-${weekDay.end}`;
  }

  const handleToday = () => {
    const first = moment().day(0);
    weekDay.start = moment(first).format('D/M/YYYY');
    weekDay.end = moment(first).add(6, 'd').format('D/M/YYYY');
    setWeekDay({ ...weekDay });
    setWeekList([...Week(first)]);
    setFirstDay(first);
    (async () => {
      await getRosterAPI();
    })();
    setSelectDate(moment());
    sessionStorage.setItem('dutyInfo', JSON.stringify({ date: moment(), id: -1, index: 0 }));
  };

  const handlePreviousWeek = () => {
    const first = moment(firstDay).day(-7);
    weekDay.start = moment(first).format('D/M/YYYY');
    weekDay.end = moment(first).add(6, 'd').format('D/M/YYYY');
    setWeekDay({ ...weekDay });
    setWeekList([...Week(first)]);
    setFirstDay(first);
    (async () => {
      await getRosterAPI();
    })();
  };

  const handleNextWeek = () => {
    const first = moment(firstDay).day(7);
    weekDay.start = moment(first).format('D/M/YYYY');
    weekDay.end = moment(first).add(6, 'd').format('D/M/YYYY');
    setWeekDay({ ...weekDay });
    setWeekList([...Week(first)]);
    setFirstDay(first);
    (async () => {
      await getRosterAPI();
    })();
  };

  const handleEdit = (id, text) => {
    (async () => {
      await updateTemplateAPI({ id, text });
    })();
  };

  const handleDelete = (id) => {
    (async () => {
      await deleteTemplateAPI({ id });
    })();
  };

  const handleSelectDate = (value) => {
    console.log(
      'value',
      moment(value).isBetween(moment('1976-01-01', 'yyyy-MM-DD'), moment('9999-12-31', 'yyyy-MM-DD'))
    );
    console.log('value', moment(value).format('yyyy-MM-DD'));
    setSelectDate(value);
    if (moment(value).isBetween(moment('1976-01-01', 'yyyy-MM-DD'), moment('9999-12-31', 'yyyy-MM-DD'))) {
      const first = moment(value).day(0);
      weekDay.start = moment(first).format('D/M/YYYY');
      weekDay.end = moment(first).add(6, 'd').format('D/M/YYYY');
      setWeekDay({ ...weekDay });
      setWeekList([...Week(first)]);
      setFirstDay(first);
      (async () => {
        await getRosterAPI();
      })();
      sessionStorage.setItem('dutyInfo', JSON.stringify({ date: value, id: -1, index: 0 }));
    }
  };

  const handleDragEnd = useCallback(
    (date) => (e) => {
      if (!e.destination || e.source.index === e.destination.index) {
        return;
      }
      let list = _.filter(roster, (item) => item.date === date);
      let orderList = _.orderBy(list, ['rosterOrder'], ['asc']);
      let swapList = reorder(orderList, e.source.index, e.destination.index);
      let newList = _.map(swapList, (item, index) => {
        return {
          dutyTemplateRosterId: item.id,
          rosterOrder: index,
        };
      });
      (async () => {
        await updateTemplateOrderAPI(newList);
      })();
    },
    [roster]
  );

  return (
    <div className={classes.root}>
      <Alert
        open={deleteSuccessDialogOpen}
        id="DutyAssign-Alert-aib"
        onClose={() => {
          setDeleteSuccessDialogOpen(false);
        }}
        text={i18n.t('DUTYASSIGN_ROSTER_DELETE_SUCCESS')}
        type="success"
      />
      <Alert
        open={copySuccessDialogOpen}
        id="DutyAssign-Alert-aib"
        onClose={() => {
          setCopySuccessDialogOpen(false);
        }}
        text={i18n.t('DUTYASSIGN_ROSTER_COPY_SUCCESS')}
        type="success"
      />
      <Header text={i18n.t('DUTYASSIGN_BACK')} path={rou.dutyassignRou.path} />
      <div className={classes.body}>
        <Head
          DateStr={DateStr}
          handleToday={handleToday}
          id="DutyAssign-Head-afo"
          handlePreviousWeek={handlePreviousWeek}
          handleNextWeek={handleNextWeek}
          title={i18n.t('DUTYASSIGN_PLANNER_TITLE')}
        >
          <DatePicker selectDate={selectDate} handleSelectDate={handleSelectDate} id="DutyAssign-DatePicker-afp" />
          <Link className={classes.editButton} to={rou.TemplateRou.path} id="DutyAssign-Link-1">
            {i18n.t('DUTYASSIGN_PLANNER_LINK')}
          </Link>
        </Head>
        <Typography className={classes.smallTitle}>{i18n.t('DUTYASSIGN_PLANNER_SUBTITLE')}</Typography>
        <div className={classes.plan}>
          {weekList.map((item, index) => (
            <DragDropContext onDragEnd={handleDragEnd(item.time)} key={item.date}>
              <Droppable droppableId={`roster-planner-${index}`}>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    <Day num={item.date} mark={item.mark} index={index}>
                      {_.map(
                        _.orderBy(
                          _.filter(roster, (template) => {
                            return template.date === item.time;
                          }),
                          ['rosterOrder'],
                          ['asc']
                        ),
                        (template, templateIndex) => (
                          <Draggable
                            draggableId={template.id.toString()}
                            key={template.id.toString()}
                            index={templateIndex}
                          >
                            {(dragProvided) => (
                              <div
                                {...dragProvided.draggableProps}
                                {...dragProvided.dragHandleProps}
                                ref={dragProvided.innerRef}
                              >
                                <Card
                                  title={template.title}
                                  context={template.context}
                                  template={template.template}
                                  handleEdit={handleEdit}
                                  handleDelete={handleDelete}
                                  key={template.id}
                                  id={template.id}
                                  rosterOrder={template.rosterOrder}
                                  date={item.time}
                                  roster={roster}
                                  templateIndex={templateIndex}
                                  setRoster={setRoster}
                                  updateTemplateOrderAPI={updateTemplateOrderAPI}
                                  handleBatchDelete={handleBatchDelete}
                                  handleCopy={handleCopy}
                                />
                              </div>
                            )}
                          </Draggable>
                        )
                      )}
                      {provided.placeholder}
                      <AddCard
                        time={item.time}
                        wardlist={list.wardlist}
                        createTemplateAPI={createTemplateAPI}
                        id={`DutyAssign-AddCard-afq-${index}`}
                        roster={roster}
                      />
                    </Day>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ))}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  rouList: state.rouList,
});

export default connect(mapStateToProps)(RosterPlanner);

export { Head };
